/* eslint-disable no-alert */
import React, { useState, useEffect } from 'react';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import {
  Row, Col, Card, CardBody,
  Nav, NavItem, NavLink, TabContent, TabPane,
} from 'reactstrap';
import moment from 'moment';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

import classnames from 'classnames';
import { formatTime } from '../../utilities/timeFormat';
import { Can } from '../../utilities/Can';
import BaseGrid from '../../components/Common/BaseGrid';
import { post } from '../../helpers/api_helper';

import { changeSidebarType } from '../../store/actions';
import PicklistGenerateModal from '../../components/Order/PicklistGenerateModal';

function formatLatestStatus(status, order) {
  if (!status) return '';

  const details = [formatTime(order.latest_status_at), order.latest_status_user]
    .filter(Boolean)
    .join(' by ');

  return (
    <>
      <div>{status}</div>
      <small className="text-muted">{details}</small>
    </>
  );
}

function formatStatusCheckbox(timestamp) {
  if (timestamp) {
    return '<span class="mdi mdi-checkbox-marked-outline text-success fs-3" />';
  }

  return '<span class="mdi mdi-checkbox-blank-outline text-default fs-3" />';
}

function formatCartSummaries(summaries) {
  if (!Array.isArray(summaries) || summaries.length === 0) return '';

  return summaries.map((summary) => {
    const details = [formatTime(summary.latest_update_at), summary.latest_user]
      .filter(Boolean)
      .join(' by ');

    return (
      <div key={summary.cart} className="mb-1">
        <div><strong>{summary.cart}</strong></div>
        <div>{`${summary.packed_items}/${summary.total_items} items packed`}</div>
        {details && <small className="text-muted">{details}</small>}
      </div>
    );
  });
}

function renderPrioritySwitch(value, order, t, prioritySavingId, onToggle) {
  return (
    <div className="form-check form-switch mb-0 d-inline-block">
      <input
        className="form-check-input"
        type="checkbox"
        role="switch"
        aria-label={t('Priority')}
        checked={Number(value) === 1}
        disabled={prioritySavingId === order.id}
        onChange={() => onToggle(order)}
      />
    </div>
  );
}

function Orders(props) {
  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
    timeOut: 8000,
  };

  const { t } = props;

  const filterMap = {
    awaiting: 'void=0&shipped=0',
    stale: 'stale=1',
    voided: 'void=1',
    shipped: 'shipped=1',
    all: '',
  };

  const [signal, setSignal] = useState(0);
  const [filters, setFilters] = useState(filterMap.awaiting);
  const [statusActiveTab, setStatusActiveTab] = useState('awaiting');
  const [activeTab, setActiveTab] = useState('1');
  const [genModalOpen, setGenModalOpen] = useState(false);
  const [prioritySavingId, setPrioritySavingId] = useState(null);
  const flaggedFilters = 'flag=1&void=0&shipped=0';
  const staleFilters = filterMap.stale;

  const voidOrder = async (order) => {
    if (window.confirm(t('Are you sure you want to void this order?'))) {
      try {
        await post(`/api/orders/save/${order.id}`, { void: true });

        toastr.success(`Order #${order.reference} successfully voided`, 'Success');
      } catch (error) {
        toastr.error(error.message, 'Error');
      } finally {
        setSignal(signal + 1);
      }
    }
  };

  const togglePriority = async (order) => {
    const nextPriority = Number(order.priority) === 1 ? 0 : 1;

    try {
      setPrioritySavingId(order.id);
      await post(`/api/orders/save/${order.id}`, { priority: nextPriority });
      toastr.success(`Order #${order.reference} priority updated`, 'Success');
    } catch (error) {
      toastr.error(error.message, 'Error');
    } finally {
      setPrioritySavingId(null);
      setSignal(signal + 1);
    }
  };

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const printPicklist = (picklist) => {
    window.open(`/orders/picklist/${picklist.id}`, '_blank');
  };

  const completePicklist = async (picklist) => {
    if (window.confirm(t('Are you sure you want to mark this picklist as complete?'))) {
      try {
        await post(`/api/orders/picklist/complete/${picklist.id}`);
        toastr.success(`Picklist #${picklist.id} marked as complete`, 'Success');
        setSignal(signal + 1);
      } catch (error) {
        toastr.error(error.message, 'Error');
      }
    }
  };

  useEffect(() => {
    // props.changeSidebarType('default');
  }, []);

  useEffect(() => {
    setFilters(filterMap[statusActiveTab]);
  }, [statusActiveTab]);

  const orderGridConfig = {
    columns: {
      flag: {
        label: 'Flag',
        html: true,
        sort: true,
        alt: 'flag_message',
        format: (f) => {
          if (f === 1) {
            return '<span class="mdi mdi-flag text-danger fs-3" />';
          }

          return '<span />';
        },
      },
      priority: {
        label: t('Priority'),
        sort: true,
        translate: false,
        format: (value, order) => renderPrioritySwitch(
          value,
          order,
          t,
          prioritySavingId,
          togglePriority,
        ),
      },
      reference: {
        label: 'Order Number',
        sort: true,
        link: {
          to: '/orders/%id%',
        },
      },
      paid_at: {
        label: 'Paid',
        date: {
          format: (date) => (formatTime(date)),
          timezone: process.env.REACT_APP_LOCAL_TZ,
        },
        sort: true,
      },
      created_at: {
        label: 'Imported',
        date: {
          format: (date) => (moment(date).fromNow()),
          timezone: process.env.REACT_APP_LOCAL_TZ,
        },
        sort: true,
      },
      ship_method: {
        label: 'Ship Method',
        sort: false,
        style: { maxWidth: '150px' },
      },
      customer_note: {
        label: 'Customer Note',
        sort: false,
        style: { maxWidth: '150px' },
      },
      customer_name: {
        label: 'Customer Name',
        format: (x, ord) => (ord.soldTo ? `${ord.soldTo.firstname} ${ord.soldTo.lastname}` : `${ord.shipTo.firstname} ${ord.shipTo.lastname}`),
      },
      items: {
        label: 'Items',
        sort: false,
        format: (x) => {
          if (x !== undefined) {
            return Math.round(x.reduce((sum, item) => sum + item.qty, 0));
          }

          return 0;
        },
      },
      latest_status: {
        label: 'Status',
        sort: false,
        format: formatLatestStatus,
      },
      picking_at: {
        label: 'Picking',
        sort: true,
        html: true,
        format: formatStatusCheckbox,
      },
      picked_at: {
        label: 'Picked',
        sort: true,
        html: true,
        format: formatStatusCheckbox,
      },
      packing_at: {
        label: 'Packing',
        sort: true,
        html: true,
        format: formatStatusCheckbox,
      },
      packed_at: {
        label: t('Packed'),
        sort: true,
        html: true,
        format: formatStatusCheckbox,
      },
      shipped_at: {
        label: 'Shipped',
        sort: true,
        html: true,
        format: formatStatusCheckbox,
      },
      void: {
        label: 'Void',
        sort: false,
        html: true,
        format: (x) => {
          if (x === 1) {
            return '<strong class="mdi mdi-cancel text-danger fs-3" />';
          }

          return '<span />';
        },
      },
      actions: {
        label: props.t('Actions'),
        actions: [
          {
            key: 'void',
            icon: 'mdi mdi-cancel',
            class: 'btn btn-sm btn-danger btn-soft-danger',
            alt: props.t('Void'),
            click: voidOrder,
            condition: (x) => (x.void === false && x.shipped === false),
          },
        ],
      },
    },
    pagination: {
      sort: 'created_at',
      direction: 'desc',
      perPage: 30,
    },
  };

  const staleOrderGridConfig = {
    ...orderGridConfig,
    columns: Object.entries(orderGridConfig.columns).reduce((columns, [key, column]) => ({
      ...columns,
      [key]: column,
      ...(key === 'latest_status' ? {
        cart_summaries: {
          label: 'Cart',
          sort: false,
          translate: false,
          format: formatCartSummaries,
        },
      } : {}),
    }), {}),
  };

  const deficitGridConfig = {
    columns: {
      stock_id: {
        label: 'Stock ID',
        sort: true,
      },
      sj_sku: {
        label: 'SJ Ref',
        sort: false,
      },
      part_number: {
        label: 'Part Number',
        sort: true,
      },
      ship_desc: {
        label: 'Description',
        sort: false,
        style: { minWidth: '200px' },
      },
      shelf_qty: {
        label: 'Qty On Shelf',
        sort: true,
      },
      needed_qty: {
        label: 'Qty Needed',
        sort: true,
      },
      deficit_qty: {
        label: 'Deficit Qty',
        sort: true,
      },
      waiting_orders_html: {
        label: 'Awaiting Orders',
        html: true,
        sort: false,
        alt: 'waiting_orders_text',
        style: { minWidth: '240px' },
      },
    },
    pagination: {
      sort: 'deficit_qty',
      direction: 'desc',
      perPage: 30,
    },
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Row>
          <Col xxl="12">
            <Nav tabs className="border-default">
              <NavItem>
                <NavLink
                  style={{ cursor: 'pointer' }}
                  className={`${classnames({
                    active: (activeTab === '1'),
                  })} border-default`}
                  onClick={() => {
                    toggle('1');
                  }}
                >
                  {t('Orders')}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  style={{ cursor: 'pointer' }}
                  className={`${classnames({
                    active: (activeTab === '2'),
                  })} border-default`}
                  onClick={() => {
                    toggle('2');
                  }}
                >
                  {t('Picklists')}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  style={{ cursor: 'pointer' }}
                  className={`${classnames({
                    active: (activeTab === '3'),
                  })} border-default`}
                  onClick={() => {
                    toggle('3');
                  }}
                >
                  {t('Flagged')}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  style={{ cursor: 'pointer' }}
                  className={`${classnames({
                    active: (activeTab === '4'),
                  })} border-default`}
                  onClick={() => {
                    toggle('4');
                  }}
                >
                  {t('Stale')}
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent
              activeTab={activeTab}
            >
              <TabPane tabId="1">
                <Card>
                  <CardBody>
                    <Row>
                      <Col xl="4" />
                      <Col xl="8">
                        <Nav tabs className="nav-tabs-custom nav-justified">
                          <NavItem>
                            <NavLink
                              style={{ cursor: 'pointer' }}
                              className={classnames({
                                active: statusActiveTab === 'awaiting',
                              })}
                              onClick={() => {
                                setStatusActiveTab('awaiting');
                              }}
                            >
                              <span className="d-block d-sm-none">
                                <i className="fas fa-home" />
                              </span>
                              <span className="d-none d-sm-block">{t('Awaiting Fulfillment')}</span>
                            </NavLink>
                          </NavItem>
                          <NavItem>
                            <NavLink
                              style={{ cursor: 'pointer' }}
                              className={classnames({
                                active: statusActiveTab === 'voided',
                              })}
                              onClick={() => {
                                setStatusActiveTab('voided');
                              }}
                            >
                              <span className="d-block d-sm-none">
                                <i className="far fa-user" />
                              </span>
                              <span className="d-none d-sm-block">{t('Voided')}</span>
                            </NavLink>
                          </NavItem>
                          <NavItem>
                            <NavLink
                              style={{ cursor: 'pointer' }}
                              className={classnames({
                                active: statusActiveTab === 'shipped',
                              })}
                              onClick={() => {
                                setStatusActiveTab('shipped');
                              }}
                            >
                              <span className="d-block d-sm-none">
                                <i className="far fa-user" />
                              </span>
                              <span className="d-none d-sm-block">{t('Shipped')}</span>
                            </NavLink>
                          </NavItem>
                          <NavItem>
                            <NavLink
                              style={{ cursor: 'pointer' }}
                              className={classnames({
                                active: statusActiveTab === 'all',
                              })}
                              onClick={() => {
                                setStatusActiveTab('all');
                              }}
                            >
                              <span className="d-block d-sm-none">
                                <i className="far fa-user" />
                              </span>
                              <span className="d-none d-sm-block">{t('All')}</span>
                            </NavLink>
                          </NavItem>
                        </Nav>
                      </Col>
                    </Row>

                    <Can i="see" this="orders">
                      <BaseGrid
                        url="/api/orders"
                        shouldUpdate={signal}
                        filters={filters}
                        hasSearch
                        config={orderGridConfig}
                      />
                    </Can>

                    <Can not i="see" this="orders"><p>{props.t('Unauthorized')}</p></Can>
                  </CardBody>
                </Card>
              </TabPane>

              <TabPane tabId="2">
                <Card>
                  <CardBody>
                    <Can i="see" this="orders">
                      <BaseGrid
                        url="/api/orders/picklist"
                        shouldUpdate={signal}
                        createText={t('Generate')}
                        create={() => { setGenModalOpen(true); }}
                        config={
                        {
                          columns: {
                            id: {
                              label: 'Picklist Number',
                              sort: true,
                            },
                            route_type: {
                              label: 'Route Type',
                            },
                            created_at: {
                              label: 'Generated',
                              date: {
                                format: (date) => (moment(date).fromNow()),
                                timezone: process.env.REACT_APP_LOCAL_TZ,
                              },
                              sort: true,
                            },
                            generated_by: {
                              label: 'Generated By',
                            },
                            items: {
                              label: 'Items',
                              sort: false,
                              format: (x) => x.length,
                            },
                            complete: {
                              label: 'Complete',
                              sort: false,
                              html: true,
                              format: (x, y) => {
                                if (y.items.length === y.items
                                  .filter((z) => z.picked_at !== null).length
                                ) {
                                  return '<span class="mdi mdi-checkbox-marked-outline text-success fs-3" />';
                                }

                                return '<span class="mdi mdi-checkbox-blank-outline text-default fs-3" />';
                              },
                            },
                            actions: {
                              label: props.t('Actions'),
                              actions: [
                                {
                                  key: 'download-print',
                                  icon: 'mdi mdi-printer',
                                  class: 'btn btn-sm btn-primary btn-soft-primary p-1 fs-4',
                                  alt: props.t('Print'),
                                  click: printPicklist,
                                },
                                {
                                  key: 'complete',
                                  icon: 'mdi mdi-checkbox-marked-outline',
                                  class: 'btn btn-sm btn-success btn-soft-success p-1 fs-4',
                                  alt: props.t('Complete'),
                                  click: completePicklist,
                                  condition: (x) => x.items.filter(
                                    (y) => y.picked_at !== null,
                                  ).length > 0,
                                },
                              ],
                            },
                          },
                          pagination: {
                            sort: 'id',
                            direction: 'desc',
                            perPage: 30,
                          },
                        }
                        }
                      />
                    </Can>

                    <Can not i="see" this="orders"><p>{props.t('Unauthorized')}</p></Can>
                  </CardBody>
                </Card>
              </TabPane>

              <TabPane tabId="3">
                <Card>
                  <CardBody>
                    <Can i="see" this="orders">
                      <h5 className="mb-3">{t('Flagged Orders')}</h5>
                      <BaseGrid
                        url="/api/orders"
                        shouldUpdate={signal}
                        filters={flaggedFilters}
                        hasSearch
                        config={orderGridConfig}
                      />

                      <h5 className="mb-3 mt-4">{t('Deficit Items')}</h5>
                      <BaseGrid
                        url="/api/orders/flagged/deficits"
                        shouldUpdate={signal}
                        hasSearch
                        config={deficitGridConfig}
                      />
                    </Can>

                    <Can not i="see" this="orders"><p>{props.t('Unauthorized')}</p></Can>
                  </CardBody>
                </Card>
              </TabPane>

              <TabPane tabId="4">
                <Card>
                  <CardBody>
                    <Can i="see" this="orders">
                      <BaseGrid
                        url="/api/orders"
                        shouldUpdate={signal}
                        filters={staleFilters}
                        hasSearch
                        config={staleOrderGridConfig}
                      />
                    </Can>

                    <Can not i="see" this="orders"><p>{props.t('Unauthorized')}</p></Can>
                  </CardBody>
                </Card>
              </TabPane>
            </TabContent>
          </Col>
        </Row>

        <PicklistGenerateModal
          onSuccess={
            () => { setSignal(signal + 1); setGenModalOpen(false); }
          }
          shown={genModalOpen}
          onClose={() => { setGenModalOpen(false); }}
        />
      </div>
    </div>
  );
}

Orders.defaultProps = {
  t: (x) => (x),
};

Orders.propTypes = {
  t: PropTypes.any,
};

const mapStateToProps = (state) => ({ ...state.Layout });

export default connect(mapStateToProps, {
  changeSidebarType,
})(withTranslation()(Orders));
